import React from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays fallback UI.
 * Always logs to console (both dev & prod) and exposes a "Show Details" toggle
 * so engineers can diagnose crashes without opening DevTools.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Always log — both dev and production so server-side log collectors see it
    console.error('[ErrorBoundary] Caught unhandled error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
  }

  handleRefresh = () => { window.location.reload(); };
  handleGoHome  = () => { window.location.href = '/dashboard'; };
  toggleDetails = () => { this.setState(s => ({ showDetails: !s.showDetails })); };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      const msg   = error?.toString() || 'Unknown error';
      const stack = errorInfo?.componentStack || '';

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4" dir="rtl">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              حدث خطأ غير متوقع
            </h1>

            <p className="text-slate-600 mb-4">
              نعتذر عن هذا الخطأ. يرجى تحديث الصفحة أو العودة للرئيسية.
            </p>

            {/* Always-visible error type */}
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 mb-4 text-right">
              <p className="text-xs font-mono text-red-700 break-all leading-relaxed">{msg}</p>
            </div>

            {/* Expandable stack trace — visible in any environment */}
            <button
              onClick={this.toggleDetails}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mx-auto mb-4"
            >
              {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showDetails ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
            </button>

            {showDetails && stack && (
              <div className="bg-slate-900 rounded-lg p-4 mb-4 text-right max-h-64 overflow-y-auto">
                <pre className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{stack}</pre>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRefresh} variant="outline" className="gap-2">
                <RefreshCw size={18} />
                تحديث الصفحة
              </Button>
              <Button onClick={this.handleGoHome} className="gap-2">
                <Home size={18} />
                الصفحة الرئيسية
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
